// What changed in the world between two dates, for turn notifications (services layer).
//
//   window (from, to]   the turn: `from` is the previous date, `to` the new one
//
//   wars ────────── start, end, status change, key developments
//   control ─────── episode start / end, the front-line area moved between series snapshots, and
//                   the start of a stretch where the series has no picture of its own (Ukraine
//                   2022-02-24 → 2022-04-03: the map keeps the last known line)
//   memberships ─── joined, left, status changed (a member → suspended pair on one day is one line)
//   leaders ─────── new head of state / government / paramount leader (same person again: skipped)
//   borders ─────── de jure changes, and the leader's own point-of-view claims taking effect
//   milestones ──── space and tech ladder rungs
//   wonders ─────── World Heritage sites, on 1 January of their inscription year (data has the year only)
//   relations ───── the leader's diplomatic state with a country moving into or out of war / ally / denounced
//
// Every change carries the sources of the fact behind it. Priority is on a 0..100 scale:
//
//   base by kind (BASE below) + 20 if it names the leader's state + 10 if it names a state at war with,
//   allied to, or denouncing the leader's state, capped at MAX_PRIORITY.
//
// A change about a nation is placed on that nation's capital hex (the camera target), a border
// change on one of the moved hexes, control on the middle hex of the held area.

import type {
  BorderChange, ControlEpisode, ControlSeries, Country, DateStr, LeaderTerm, Membership, Milestone, PovOverrides, SourceId, War,
  Wonder, WorldState,
} from '../core/schema.ts';
import { addDays, daysBetween, inSpan, yearOf } from '../core/dates.ts';
import type { Relation, RelationInfo } from './api.ts';
import { indexOf, type ActorTable } from './actors.ts';
import { noteAsOf } from './asof.ts';
import { citedOrAll, gapAt, snapAt, type ControlModel, type SeriesLayer } from './control.ts';
import type { PovKey } from './owners.ts';
import { dayOf, uniq } from './util.ts';
import { sidesAt, warStatus, ONGOING } from './wars.ts';

export type ChangeKind = 'war' | 'control' | 'border' | 'membership' | 'leader' | 'milestone' | 'wonder' | 'relation';

export interface WorldChange {
  /** Stable for the same change, so a repeated query does not double-post it. */
  id: string;
  kind: ChangeKind;
  date: DateStr;
  title: string;
  body: string;
  /** Camera target. */
  hex?: number;
  iso3s: string[];
  sources: SourceId[];
  /** 0..100. */
  priority: number;
  /** Relation changes only: the leader's relation to the other state before and after. */
  relation?: { from: Relation; to: Relation };
}

export interface ChangeInputs {
  world: WorldState;
  borders: BorderChange[];
  pov: PovOverrides;
  milestones: Milestone[];
  wonders: Wonder[];
  countries: Map<string, Country>;
  actors: ActorTable;
  control: ControlModel;
  /** Relation of every actor index to `iso3` at a date (cached by the world). */
  relations(date: DateStr, iso3: string): Map<number, RelationInfo>;
}

export interface Viewpoint {
  iso3: string;
  /** Null for a state view (sandbox): no point-of-view claims. */
  pov: PovKey | null;
}

const MAX_PRIORITY = 100;
const OWN_STATE_BONUS = 20;
const NEIGHBOUR_BONUS = 10;

const BASE = {
  warStart: 80,
  warEnd: 75,
  warStatus: 70,
  development: 60,
  controlEnd: 70,
  controlStart: 65,
  frontMove: 60,
  dataGap: 45,
  border: 60,
  claim: 55,
  membership: 50,
  leader: 55,
  milestone: 45,
  wonder: 35,
  relationWar: 85,
  relationAlly: 60,
  relationDenounced: 50,
} as const;

/** Front-line moves under this many km² between snapshots are not worth a notification. */
const MIN_FRONT_MOVE_KM2 = 100;
const TITLE_MAX = 110;
/** Episodes this close end to start are one occupation changing status (Crimea 2014-03-17 → 03-18). */
const CONTINUATION_DAYS = 1;
const FRONT_SERIES_KM2 = 'km²';

const CONTROL_START: Record<ControlEpisode['status'], string> = {
  occupied: 'occupies',
  contested: 'contests',
  'annexation-claimed': 'claims by annexation',
  breakaway: 'holds breakaway control of',
};

const CONTROL_END: Record<ControlEpisode['status'], string> = {
  occupied: 'occupation of',
  contested: 'contest over',
  'annexation-claimed': 'annexation claim on',
  breakaway: 'breakaway control of',
};

const ROLE: Record<LeaderTerm['role'], string> = {
  head_of_state: 'head of state',
  head_of_government: 'head of government',
  paramount_leader: 'paramount leader',
};

// ─── helpers ─────────────────────────────────────────────────────────────────────────────

function nameOf(inp: ChangeInputs, id: string): string {
  const i = inp.actors.byId.get(id);
  return (i !== undefined ? inp.actors.list[i]?.name : undefined) ?? id;
}

function inWindow(d: DateStr, from: DateStr, to: DateStr): boolean {
  return d > from && d <= to;
}

/** First clause of a sentence, so a curated note can serve as a title. */
function clause(text: string): string {
  const cut = text.search(/[;(]|\. /);
  const head = (cut > 0 ? text.slice(0, cut) : text).trim();
  return head.length > TITLE_MAX ? `${head.slice(0, TITLE_MAX - 1).trimEnd()}…` : head;
}

/** Countries among `ids` (an actor list may also hold militias and blocs). */
function countriesIn(inp: ChangeInputs, ids: string[]): string[] {
  return uniq(ids.filter((id) => inp.countries.has(id)));
}

function capitalHex(inp: ChangeInputs, iso3s: string[]): number | undefined {
  for (const id of iso3s) {
    const hex = inp.countries.get(id)?.capital?.hex;
    if (hex !== undefined) {
      return hex;
    }
  }
  return undefined;
}

function middle(hexes: ArrayLike<number>): number | undefined {
  return hexes.length === 0 ? undefined : hexes[Math.floor(hexes.length / 2)];
}

// ─── wars ────────────────────────────────────────────────────────────────────────────────

/** The states of the war as of `date`: a party that joined later is not named in what happened before. */
function warIso3s(inp: ChangeInputs, w: War, date: DateStr): string[] {
  const sides = sidesAt(w, date);
  return countriesIn(inp, [...sides.a, ...sides.b, ...w.theatre]);
}

function warChanges(inp: ChangeInputs, from: DateStr, to: DateStr): WorldChange[] {
  const out: WorldChange[] = [];

  for (const w of inp.world.wars) {
    // The states named, and the hex they are placed on, follow the parties of the day the change is dated.
    const at = (date: DateStr): Pick<WorldChange, 'iso3s' | 'hex'> => {
      const iso3s = warIso3s(inp, w, date);
      return { iso3s, hex: capitalHex(inp, uniq([...w.theatre, ...iso3s])) };
    };
    const sources = uniq([...w.sources, ...w.status.sources]);
    const startsNow = inWindow(w.from, from, to);
    const endsNow = w.to !== null && inWindow(w.to, from, to);

    if (startsNow) {
      const opening = w.events.filter((e) => e.date === w.from).map((e) => e.text).join(' ');
      out.push({
        id: `chg:war:${w.id}:start`, kind: 'war', date: w.from, title: `${w.name} begins`, body: opening || clause(w.name),
        ...at(w.from), sources: uniq([...sources, ...w.events.filter((e) => e.date === w.from).flatMap((e) => e.sources)]),
        priority: BASE.warStart,
      });
    }

    if (endsNow && w.to !== null) {
      out.push({
        id: `chg:war:${w.id}:end`, kind: 'war', date: w.to, title: `${w.name} ends`,
        body: `The war is recorded as ended on ${w.to}.`, ...at(addDays(w.to, -1)), sources, priority: BASE.warEnd,
      });
    }

    // A change between two dated claims; a war of unknown phase ('ongoing') has nothing to report.
    const before = warStatus(w, from)?.value;
    const after = warStatus(w, to)?.value;
    if (before && after && before !== after && before !== ONGOING && after !== ONGOING && !endsNow) {
      out.push({
        id: `chg:war:${w.id}:status:${after}`, kind: 'war', date: to, title: `${w.name}: ${before} → ${after}`,
        body: `Status of the war changed from ${before} to ${after}.`, ...at(to), sources, priority: BASE.warStatus,
      });
    }

    for (const [i, e] of w.events.entries()) {
      // The opening line already sits in the start notification.
      if (!inWindow(e.date, from, to) || (startsNow && e.date === w.from)) {
        continue;
      }
      out.push({
        id: `chg:war:${w.id}:dev:${i}`, kind: 'war', date: e.date, title: `${w.name}: ${clause(e.text)}`, body: e.text,
        ...at(e.date), sources: uniq([...e.sources, ...w.sources]), priority: BASE.development,
      });
    }
  }
  return out;
}

// ─── control ─────────────────────────────────────────────────────────────────────────────

/** Hex of an episode's held area at `date`: the middle one of its layer or latest snapshot. */
function heldHex(inp: ChangeInputs, ep: ControlEpisode, date: DateStr): number | undefined {
  const layer = inp.control.layers.find((l) => l.episode === ep);
  if (layer) {
    return middle(layer.hexes);
  }
  const s = inp.control.series.find((x) => x.episode === ep);
  const k = s ? snapAt(s.series, date) : -1;
  return s && k >= 0 ? middle(s.snaps[k].hexes) : undefined;
}

function snapSources(snap: ControlSeries['snapshots'][number]): SourceId[] {
  return snap.sources?.length ? snap.sources : [snap.source];
}

/** The hex whose held share changed most between two snapshots of a series. */
function largestShift(s: SeriesLayer, k0: number, k1: number): number | undefined {
  const before = new Map<number, number>();
  if (k0 >= 0) {
    s.snaps[k0].hexes.forEach((h, j) => before.set(h, s.snaps[k0].frac[j]));
  }

  let best: number | undefined;
  let bestDelta = -1;
  const after = s.snaps[k1];
  after.hexes.forEach((h, j) => {
    const delta = Math.abs(after.frac[j] - (before.get(h) ?? 0));
    if (delta > bestDelta) {
      bestDelta = delta;
      best = h;
    }
  });
  return best;
}

function frontMoves(inp: ChangeInputs, from: DateStr, to: DateStr): WorldChange[] {
  const out: WorldChange[] = [];

  for (const s of inp.control.series) {
    const k0 = snapAt(s.series, from);
    const k1 = snapAt(s.series, to);
    const live = inSpan(s.episode, to) && inSpan(s.episode, from);
    // No earlier picture means the episode's start line covers it.
    if (!live || k0 < 0 || k1 <= k0) {
      continue;
    }

    const then = s.series.snapshots[k0];
    const now = s.series.snapshots[k1];
    const delta = now.area_km2 - then.area_km2;
    // A picture older than the episode is another period's (Ukraine 2018 under the 2022 invasion):
    // the gap is data missing, not ground gained.
    if (then.date < s.episode.from || Math.abs(delta) < MIN_FRONT_MOVE_KM2) {
      continue;
    }

    const who = nameOf(inp, s.series.controller);
    const verb = delta > 0 ? 'gains' : 'loses';
    const km2 = Math.round(Math.abs(delta)).toLocaleString('en-US');
    out.push({
      id: `chg:control:${s.episode.id}:front:${now.date}`,
      kind: 'control',
      date: now.date,
      title: `${who} ${verb} ${km2} ${FRONT_SERIES_KM2} in ${nameOf(inp, s.series.territory_of)}`,
      body: `Held area moved from ${Math.round(then.area_km2).toLocaleString('en-US')} to ${Math.round(now.area_km2).toLocaleString('en-US')} ${FRONT_SERIES_KM2} between the ${then.date} and ${now.date} snapshots.`,
      hex: largestShift(s, k0, k1),
      iso3s: countriesIn(inp, [s.series.controller, s.series.territory_of]),
      sources: citedOrAll(inp.control, uniq([...snapSources(now), ...snapSources(then)]), now.date),
      priority: BASE.frontMove,
    });
  }
  return out;
}

/** An episode of the same controller over the same territory starting right after this one ends. */
function continuedBy(inp: ChangeInputs, ep: ControlEpisode): boolean {
  if (ep.to === null) {
    return false;
  }
  const end = dayOf(ep.to);
  return inp.world.control.some(
    (o) => o !== ep && o.controller === ep.controller && o.territory_of === ep.territory_of && dayOf(o.from) - end <= CONTINUATION_DAYS && dayOf(o.from) >= end,
  );
}

/**
 * An ally taking over where `ep` ends (DPR → Russia on 2022-02-24): an episode over the same
 * territory that starts within CONTINUATION_DAYS, its controller on the same side of the war that day.
 */
function handoverTo(inp: ChangeInputs, ep: ControlEpisode): ControlEpisode | undefined {
  const war = inp.world.wars.find((w) => w.id === ep.war);
  if (ep.to === null || !war) {
    return undefined;
  }

  const end = dayOf(ep.to);
  return inp.world.control.find((o) => {
    if (o === ep || o.controller === ep.controller || o.territory_of !== ep.territory_of || dayOf(o.from) < end || dayOf(o.from) - end > CONTINUATION_DAYS) {
      return false;
    }
    const sides = sidesAt(war, o.from);
    return [sides.a, sides.b].some((side) => side.includes(ep.controller) && side.includes(o.controller));
  });
}

/** The window holds the start of a series episode whose first picture of its own comes later. */
function gapChanges(inp: ChangeInputs, from: DateStr, to: DateStr): WorldChange[] {
  const out: WorldChange[] = [];

  for (const s of inp.control.series) {
    const ep = s.episode;
    const own = s.series.snapshots.find((x) => x.date >= ep.from);
    const k = snapAt(s.series, ep.from);
    if (!inWindow(ep.from, from, to) || !own || own.date <= ep.from || k < 0) {
      continue;
    }
    const last = s.series.snapshots[k];
    // A declared gap draws no front and marks the land unknown; otherwise the older line stays up.
    const declared = gapAt(s.series, ep.from);
    const kept = `Until then the map keeps the ${last.date} line, which does not show the ${ep.from} advance.`;
    const marked = 'Until then the map draws no front line and marks control of the land between the two pictures as unknown.';
    out.push({
      id: `chg:control:${ep.id}:gap`, kind: 'control', date: ep.from,
      title: `No front-line data for ${nameOf(inp, ep.territory_of)} until ${own.date}`,
      body: `The next dated picture of the held area is ${own.date}. ${declared ? marked : kept}`,
      hex: heldHex(inp, ep, to) ?? capitalHex(inp, [ep.territory_of]), iso3s: countriesIn(inp, [ep.controller, ep.territory_of]),
      sources: citedOrAll(inp.control, uniq(declared ? [...declared.sources, ...snapSources(own)] : [...snapSources(last), ...snapSources(own)]), ep.from),
      priority: BASE.dataGap,
    });
  }
  return out;
}

/**
 * The part of the state an episode covers, worded so a title never claims the whole state:
 * "part of Armenia" when the curated geometry is a partial admin-1 unit or a point, otherwise
 * "territory of Ukraine" (whole units, a front-line series or a file are areas, not the state).
 */
function coverOf(inp: ChangeInputs, ep: ControlEpisode): string {
  const g = ep.geometry;
  const partial = 'points' in g || ('admin1' in g && (g.partial?.length ?? 0) > 0);
  return `${partial ? 'part' : 'territory'} of ${nameOf(inp, ep.territory_of)}`;
}

function controlChanges(inp: ChangeInputs, from: DateStr, to: DateStr): WorldChange[] {
  const out: WorldChange[] = [];

  for (const ep of inp.world.control) {
    const iso3s = countriesIn(inp, [ep.controller, ep.territory_of]);
    const who = nameOf(inp, ep.controller);
    const where = coverOf(inp, ep);

    if (inWindow(ep.from, from, to)) {
      out.push({
        id: `chg:control:${ep.id}:start`, kind: 'control', date: ep.from, title: `${who} ${CONTROL_START[ep.status]} ${where}`,
        body: noteAsOf(ep.note ?? '', ep.from), hex: heldHex(inp, ep, to) ?? capitalHex(inp, [ep.territory_of]), iso3s,
        sources: citedOrAll(inp.control, ep.sources, ep.from), priority: BASE.controlStart,
      });
    }
    if (ep.to === null || !inWindow(ep.to, from, to) || continuedBy(inp, ep)) {
      continue;
    }

    // An ally taking over on the same day is a handover, not an end of control.
    const heir = handoverTo(inp, ep);
    const title = heir ? `Control of ${where} passes from ${who} to ${nameOf(inp, heir.controller)}` : `End of ${CONTROL_END[ep.status]} ${where} (${who})`;
    const cites = citedOrAll(inp.control, uniq([...ep.sources, ...(heir?.sources ?? [])]), ep.to);
    out.push({
      id: `chg:control:${ep.id}:end`, kind: 'control', date: ep.to, title,
      body: noteAsOf(ep.note ?? '', ep.to), hex: heldHex(inp, ep, from) ?? capitalHex(inp, [ep.territory_of]), iso3s,
      sources: cites, priority: BASE.controlEnd,
    });
  }
  return [...out, ...frontMoves(inp, from, to), ...gapChanges(inp, from, to)];
}

// ─── memberships ─────────────────────────────────────────────────────────────────────────

/** Members leaving one bloc on one day at this count or more is the bloc being reshaped (G8 → G7). */
const MASS_LEAVE = 3;

function membershipChanges(inp: ChangeInputs, from: DateStr, to: DateStr): WorldChange[] {
  const out: WorldChange[] = [];
  const rows = inp.world.memberships;
  const key = (m: Membership): string => `${m.bloc}|${m.member}`;
  const starting = new Map<string, Membership[]>();
  const ending = new Map<string, Membership[]>();
  const leaving = new Map<string, Membership[]>();

  for (const m of rows) {
    starting.set(`${key(m)}|${m.from}`, [...(starting.get(`${key(m)}|${m.from}`) ?? []), m]);
    if (m.to !== null) {
      ending.set(`${key(m)}|${m.to}`, [...(ending.get(`${key(m)}|${m.to}`) ?? []), m]);
    }
  }

  for (const m of rows) {
    const who = nameOf(inp, m.member);
    const bloc = nameOf(inp, m.bloc);
    const iso3s = countriesIn(inp, [m.member]);
    const hex = capitalHex(inp, iso3s);

    // Same day, same pair, a row ended and this one began: one status change, not leave + join.
    const previous = ending.get(`${key(m)}|${m.from}`)?.[0];
    if (inWindow(m.from, from, to)) {
      const title = previous
        ? `${who} in ${bloc}: ${previous.status} → ${m.status}`
        : m.status === 'member' ? `${who} joins ${bloc}` : `${who} becomes ${m.status} of ${bloc}`;
      out.push({
        id: `chg:membership:${key(m)}:${m.from}`, kind: 'membership', date: m.from, title, body: m.note ?? '', hex, iso3s,
        sources: uniq([...m.sources, ...(previous?.sources ?? [])]), priority: BASE.membership,
      });
    }

    const next = m.to !== null ? starting.get(`${key(m)}|${m.to}`) : undefined;
    if (m.to !== null && inWindow(m.to, from, to) && !next) {
      leaving.set(`${m.bloc}|${m.to}`, [...(leaving.get(`${m.bloc}|${m.to}`) ?? []), m]);
    }
  }

  return [...out, ...leaveChanges(inp, leaving)];
}

/** One line per bloc and day: a single leaver as such, a crowd (MASS_LEAVE+) as one reshaping. */
function leaveChanges(inp: ChangeInputs, leaving: Map<string, Membership[]>): WorldChange[] {
  const out: WorldChange[] = [];

  for (const group of leaving.values()) {
    const first = group[0];
    const date = first.to as DateStr;
    const bloc = nameOf(inp, first.bloc);
    const iso3s = countriesIn(inp, group.map((m) => m.member));
    const many = group.length >= MASS_LEAVE;
    out.push({
      id: many ? `chg:membership:${first.bloc}:${date}:left` : `chg:membership:${first.bloc}|${first.member}:${date}:left`,
      kind: 'membership', date,
      title: many ? `${group.length} members leave ${bloc}` : `${nameOf(inp, first.member)} leaves ${bloc}`,
      body: first.note ?? '', hex: capitalHex(inp, many ? iso3s : iso3s.slice(0, 1)),
      iso3s: many ? iso3s : iso3s.slice(0, 1), sources: uniq(group.flatMap((m) => m.sources)), priority: BASE.membership,
    });
  }
  return out;
}

// ─── leaders ─────────────────────────────────────────────────────────────────────────────

function leaderChanges(inp: ChangeInputs, from: DateStr, to: DateStr): WorldChange[] {
  const out: WorldChange[] = [];
  const terms = inp.world.leaders;

  for (const t of terms) {
    if (!inWindow(t.from, from, to)) {
      continue;
    }
    // The same person moving between titles of one office (Ahmed al-Sharaa: leader → president) is no change.
    const predecessor = terms.find((p) => p.country === t.country && p.role === t.role && p.to === t.from);
    if (predecessor?.name === t.name) {
      continue;
    }
    const country = nameOf(inp, t.country);
    out.push({
      id: `chg:leader:${t.country}:${t.role}:${t.from}`, kind: 'leader', date: t.from,
      title: `${t.name} becomes ${ROLE[t.role]} of ${country}`,
      body: predecessor ? `Succeeds ${predecessor.name}.` : t.note ?? '', hex: capitalHex(inp, [t.country]),
      iso3s: countriesIn(inp, [t.country]), sources: t.sources, priority: BASE.leader,
    });
  }
  return out;
}

// ─── borders, claims, milestones, wonders ────────────────────────────────────────────────

function borderChanges(inp: ChangeInputs, view: Viewpoint, from: DateStr, to: DateStr): WorldChange[] {
  const out: WorldChange[] = [];

  for (const b of inp.borders) {
    if (!inWindow(b.date, from, to)) {
      continue;
    }
    const ids = b.hexes.flatMap(([, before, after]) => [inp.actors.list[before]?.id, inp.actors.list[after]?.id]);
    out.push({
      id: `chg:border:${b.date}`, kind: 'border', date: b.date, title: clause(b.note), body: b.note, hex: middle(b.hexes.map((h) => h[0])),
      iso3s: countriesIn(inp, ids.filter((x): x is string => x !== undefined)), sources: b.sources, priority: BASE.border,
    });
  }

  // A state view has no point of view, so no claim lines.
  if (view.pov === null) {
    return out;
  }

  for (const [i, c] of inp.pov[view.pov].entries()) {
    if (!inWindow(c.since, from, to)) {
      continue;
    }
    out.push({
      id: `chg:claim:${view.pov}:${i}:${c.since}`, kind: 'border', date: c.since, title: clause(c.note), body: c.note,
      hex: middle(c.hexes.map((h) => h[0])), iso3s: [view.iso3], sources: c.sources, priority: BASE.claim,
    });
  }
  return out;
}

function milestoneChanges(inp: ChangeInputs, from: DateStr, to: DateStr): WorldChange[] {
  return inp.milestones
    .filter((m) => inWindow(m.date, from, to))
    .map((m) => ({
      id: `chg:milestone:${m.id}`, kind: 'milestone' as const, date: m.date, title: clause(m.title),
      body: m.title, hex: capitalHex(inp, [m.iso3]), iso3s: countriesIn(inp, [m.iso3]), sources: m.sources,
      priority: BASE.milestone,
    }));
}

/** Sites are dated by year only, so they arrive on the first day of that year; the card says so. */
function wonderChanges(inp: ChangeInputs, from: DateStr, to: DateStr): WorldChange[] {
  const first = yearOf(from);
  const last = yearOf(to);
  return inp.wonders
    .filter((w) => w.year > first && w.year <= last)
    .map((w) => ({
      id: `chg:wonder:${w.id}`, kind: 'wonder' as const, date: `${w.year}-01-01`, title: `${w.name} inscribed as a World Heritage site in ${w.year}`,
      body: `UNESCO ${w.category} site. The record gives the year only, so the card sits on 1 January.`, hex: w.hex, iso3s: countriesIn(inp, w.iso3s), sources: w.sources,
      priority: BASE.wonder,
    }));
}

// ─── relations ───────────────────────────────────────────────────────────────────────────

/** Friend ↔ neutral flips with each UNGA session year; only war, alliance and denunciation are news. */
function relationBase(before: RelationInfo['relation'], after: RelationInfo['relation']): number | null {
  const pair = [before, after];
  if (pair.includes('war')) {
    return BASE.relationWar;
  }
  if (pair.includes('ally')) {
    return BASE.relationAlly;
  }
  return pair.includes('denounced') ? BASE.relationDenounced : null;
}

/**
 * The first day in (from, to] on which `index` stands in the `wanted` relation: the day the record
 * behind the flip (a war's start, a sanction's `from`) took effect, not the end of the turn.
 * Bisects, so a long window costs a handful of relation lookups per flip, not one per day.
 */
function flipDay(inp: ChangeInputs, iso3: string, index: number, wanted: Relation, from: DateStr, to: DateStr): DateStr {
  let before = from;
  let after = to;

  while (daysBetween(before, after) > 1) {
    const mid = addDays(before, Math.floor(daysBetween(before, after) / 2));
    if (inp.relations(mid, iso3).get(index)?.relation === wanted) {
      after = mid;
    } else {
      before = mid;
    }
  }
  return after;
}

function relationChanges(inp: ChangeInputs, view: Viewpoint, from: DateStr, to: DateStr): WorldChange[] {
  const out: WorldChange[] = [];
  const then = inp.relations(from, view.iso3);
  const now = inp.relations(to, view.iso3);

  for (const [index, after] of now) {
    const before = then.get(index);
    const actor = inp.actors.list[index];
    if (!before || !actor || before.relation === after.relation || index > inp.actors.maxCountry) {
      continue;
    }
    const priority = relationBase(before.relation, after.relation);
    if (priority === null) {
      continue;
    }
    const date = flipDay(inp, view.iso3, index, after.relation, from, to);
    out.push({
      id: `chg:relation:${view.iso3}:${actor.id}:${date}`, kind: 'relation', date,
      title: `${actor.name}: ${before.relation} → ${after.relation}`, body: after.reason,
      hex: capitalHex(inp, [actor.id]), iso3s: uniq([view.iso3, actor.id]), sources: uniq([...after.sources, ...before.sources]),
      priority, relation: { from: before.relation, to: after.relation },
    });
  }
  return out;
}

// ─── all together ────────────────────────────────────────────────────────────────────────

/** Raise a change's priority for how close it is to the leader (see header). */
function withBonus(inp: ChangeInputs, view: Viewpoint, rel: Map<number, RelationInfo>, c: WorldChange): WorldChange {
  if (c.kind === 'relation') {
    return c;
  }
  let bonus = 0;
  if (c.iso3s.includes(view.iso3)) {
    bonus = OWN_STATE_BONUS;
  } else if (c.iso3s.some((id) => ['war', 'ally', 'denounced'].includes(rel.get(indexOf(inp.actors, id))?.relation ?? ''))) {
    bonus = NEIGHBOUR_BONUS;
  }
  return { ...c, priority: Math.min(MAX_PRIORITY, c.priority + bonus) };
}

/** Changes with the same kind, date and title are one line (two Turkish zones handed back on one day). */
function merged(list: WorldChange[]): WorldChange[] {
  const byTitle = new Map<string, WorldChange>();

  for (const c of list) {
    const key = `${c.kind}|${c.date}|${c.title}`;
    const hit = byTitle.get(key);
    if (!hit) {
      byTitle.set(key, c);
      continue;
    }
    byTitle.set(key, { ...hit, iso3s: uniq([...hit.iso3s, ...c.iso3s]), sources: uniq([...hit.sources, ...c.sources]) });
  }
  return [...byTitle.values()];
}

/** Everything that changed in (from, to] as seen by `view`, most important first. */
export function worldChanges(inp: ChangeInputs, view: Viewpoint, from: DateStr, to: DateStr): WorldChange[] {
  if (to <= from) {
    return [];
  }
  const rel = inp.relations(to, view.iso3);
  const all = [
    ...warChanges(inp, from, to),
    ...controlChanges(inp, from, to),
    ...membershipChanges(inp, from, to),
    ...leaderChanges(inp, from, to),
    ...borderChanges(inp, view, from, to),
    ...milestoneChanges(inp, from, to),
    ...wonderChanges(inp, from, to),
    ...relationChanges(inp, view, from, to),
  ];
  return merged(all)
    .map((c) => withBonus(inp, view, rel, c))
    .sort((a, b) => b.priority - a.priority || (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}
